import { useMemo } from 'react';

import useIdentities from '~/hooks/useLoaders/useIdentities';
import useIntegrations from '~/hooks/useLoaders/useIntegrations';
import type { Subscription } from '~/interfaces/subscriptions';
import useSubscriberPortalRedirectParams from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useSubscriberPortalRedirectParams';
import { isInvalidSubscription } from '~/utils/isInvalidSubscription';

import { IdentitiesContext, type IdentitiesContextType } from './IdentitiesContext';

type Props = {
  subscription: Subscription;
  subscriberId: string;
  children: React.ReactNode;
};

/**
 * Owns the connected-accounts (OAuth) data + redirect handling, mounted at the composition root
 * so it runs on every tab. Fetches the subscriber's identities and the available social-login
 * providers, and runs `useSubscriberPortalRedirectParams` (post-OAuth toast + identities cache invalidation,
 * `?intent=connect` → modal promotion, and the `?billing_portal_error=1` bounce that lands on the
 * billing/groups tab). Renders no UI; the General-tab profile card + connected-accounts modal
 * read from context.
 */
export const IdentitiesProvider = ({ subscription, subscriberId, children }: Props) => {
  const isSubscriptionInvalid = isInvalidSubscription(subscription);

  // TODO: When we add Google and Apple login, they won't come from this fetch.
  // We should make a social-login hook that fetches integrations and appends
  // Google/Apple based on a publication setting.
  const { integrations } = useIntegrations(subscriberId, !isSubscriptionInvalid);
  const { identities } = useIdentities(subscriberId, !isSubscriptionInvalid);

  useSubscriberPortalRedirectParams({ subscriberId });

  const value = useMemo<IdentitiesContextType>(
    () => ({
      integrations,
      identities,
      subscriberId,
    }),
    [integrations, identities, subscriberId],
  );

  return <IdentitiesContext.Provider value={value}>{children}</IdentitiesContext.Provider>;
};
